import React, { useState, useEffect, useMemo } from 'react';
import './../styles/SiteTreeView.css';
import siteService from './../services/SiteService';
import { useAuth } from './../context/AuthContext';
import HR from './../utils/HR';
import PlusPng from './../assets/images/icons-plus-22.png';
import MinusPng from './../assets/images/icons-minus-22.png';

const SiteTreeView = ({ onSiteClick }) => {
  const [sites, setSites] = useState([]);
  const [expandedSites, setExpandedSites] = useState({});
  const [selectedSite, setSelectedSite] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { token } = useAuth();

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.get(token);
        setSites(transformSiteData(response));
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, [token]);

  useEffect(() => {
    if (sites.length > 0 && !selectedSite) {
      setSelectedSite(sites[0]);
      onSiteClick && onSiteClick(sites[0].value);
    }
  }, [sites, selectedSite, onSiteClick]);

  const transformSiteData = (data) => {
  const norm = v => String(v).trim();

  // 1) Index all nodes
  const byId = {};
  data.forEach(s => {
    const id = norm(s.id);
    byId[id] = {
      ...s,
      id,
      label: s.sitename || s.name || String(s.id),
      value: id,
      nodes: [],
      _hasParent: false, // track if we successfully linked a parent
    };
  });

  // 2) Link using the level chain where possible
  data.forEach(s => {
    const chain = String(s.level || '')
      .split('-')
      .map(x => x.trim())
      .filter(Boolean);

    if (chain.length >= 2) {
      const currentId = norm(s.id);
      const parentId = chain[chain.length - 2]; // immediate parent in chain
      if (byId[parentId] && byId[currentId]) {
        byId[parentId].nodes.push(byId[currentId]);
        byId[currentId]._hasParent = true;
      }
    }
  });

  // 3) Fallback link using parentId if still orphaned
  data.forEach(s => {
    const id = norm(s.id);
    if (!byId[id]._hasParent) {
      const p = s.parentId != null ? norm(s.parentId) : null;
      if (p && byId[p]) {
        byId[p].nodes.push(byId[id]);
        byId[id]._hasParent = true;
      }
    }
  });

  // 4) Dedupe and sort children
  Object.values(byId).forEach(node => {
    const seen = new Set();
    node.nodes = node.nodes.filter(child => {
      if (seen.has(child.id)) return false;
      seen.add(child.id);
      return true;
    }).sort((a, b) => a.label.localeCompare(b.label));
  });

  // 5) Roots = nodes that never got a parent
  const roots = Object.values(byId)
    .filter(n => !n._hasParent)
    .sort((a, b) => a.label.localeCompare(b.label));

  return roots;
};


  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const toggleSite = (siteValue) => {
    setExpandedSites((prev) => ({
      ...prev,
      [siteValue]: !prev[siteValue],
    }));
  };

  const handleSiteClick = (site) => {
    setSelectedSite(site);
    if (onSiteClick) {
      onSiteClick(site.value);
    }
    setDropdownOpen(false);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };



  const renderTree = (site, level = 0) => (
    <>
      {level === 0 && <HR />}
      <li key={site.value} className="site-item" style={{ paddingLeft: `${level * 20}px`, marginBottom: '5px', padding: '10px' }}>
        <div className="site-item-header">
          {site.nodes.length > 0 ? (
            <button
              className="tv-toggle-button"
              onClick={() => toggleSite(site.value)}
              aria-label={expandedSites[site.value] ? 'Collapse' : 'Expand'}
            >
              {expandedSites[site.value] ? (
                <img src={MinusPng} alt="-" />
              ) : (
                <img src={PlusPng} alt="+" />
              )}
            </button>
          ) : (
            <span className="tv-toggle-placeholder"> </span>
          )}
          <span
            className={`site-label ${selectedSite?.value === site.value ? 'selected' : ''}`}
            onClick={() => handleSiteClick(site)}
          >
            {site.label}
          </span>
        </div>
        {expandedSites[site.value] && site.nodes.length > 0 && (
          <ul className="subsite-list">
            {site.nodes.map((subsite) => renderTree(subsite, level + 1))}
          </ul>
        )}
      </li>
    </>
  );

const pruneTreeBySearch = (nodes, term) => {
  const t = term.trim().toLowerCase();
  if (!t) return nodes; // no search → full tree

  const walk = (node) => {
    const label = (node.label || '').toLowerCase();
    const selfMatches = label.includes(t);

    // If this node matches, keep it with ALL its children
    if (selfMatches) {
      return node;
    }

    // Otherwise, recurse into children and keep only matching subtrees
    const filteredChildren = (node.nodes || [])
      .map(walk)
      .filter(Boolean);

    if (filteredChildren.length > 0) {
      return { ...node, nodes: filteredChildren };
    }

    return null;
  };

  return nodes.map(walk).filter(Boolean);
};

  const filteredSites = useMemo(
  () => pruneTreeBySearch(sites, searchTerm),
  [sites, searchTerm]
);
useEffect(() => {
  if (!searchTerm.trim()) return;

  const expandAll = (nodes, acc = {}) => {
    nodes.forEach(n => {
      acc[n.value] = true;
      if (n.nodes?.length) expandAll(n.nodes, acc);
    });
    return acc;
  };

  setExpandedSites(expandAll(filteredSites));
}, [searchTerm, filteredSites]);
  return (
    <div className="tv-dropdown-container">
      <button
        className={dropdownOpen ? "tv-dropdown-toggle-open" : "tv-dropdown-toggle"}
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
      >
        {selectedSite ? selectedSite.label : 'Select Site'}
        <span className="tv-dropdown-arrow">
          {dropdownOpen ? <span className="custom-icon-up">⏶</span> : <span className="custom-icon-down">⏷</span>}
        </span>
      </button>
    
  
      {dropdownOpen && sites.length > 0 && (
        <div className="tv-dropdown-menu">
        <div className="tv-search-input-container">
          <div className="tv-search-input-wrapper">
  <input
    type="text"
    placeholder="Search sites..."
    className="tv-search-input"
    value={searchTerm}
    onChange={handleSearch}
  />
  {searchTerm && (
    <button
      className="tv-clear-button"
      onClick={() => setSearchTerm('')}
      aria-label="Clear search"
    >
      ×
    </button>
  )}
</div>

          </div>
          <ul className="site-list">
            {filteredSites.map(site => (
    <li key={site.id}>
      {renderTree(site)}
    </li>
  ))}
          </ul>
        </div>
      )}
    

    </div>
  );
};

export default SiteTreeView;
